import MuiInputLabel from '@material-ui/core/InputLabel';
import MuiPaper from '@material-ui/core/Paper';
import MuiTextField from '@material-ui/core/TextField';
import MuiAutocomplete, {
  AutocompleteRenderInputParams,
} from '@material-ui/lab/Autocomplete';
import { NullableString } from 'mapistry-shared';
import React, {
  FocusEvent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import { bodyMedium } from '../styles/text';
import { SelectableOption } from '../types/SelectableOption';
import { ConditionalWrapper } from './ConditionalWrapper';
import { ErrorText, SubText } from './styled/form';
import { Tooltip } from './Tooltip';
import { VisuallyHidden } from './VisuallyHidden';

const UnderlineTextInput = styled(MuiTextField)`
  & label.Mui-focused {
    color: ${({ theme }) => theme.colors.gray999};
  }
`;

const FormTextInput = styled(MuiTextField)<{ $isCompact?: boolean }>`
  & .MuiInput-root {
    height: 2rem;
    padding: 0 0.5rem;
    font-size: 0.875rem;
    color: ${({ theme }) => theme.colors.gray333};
    background-color: ${({ theme }) => theme.colors.white};
    border: 1px solid ${({ theme }) => theme.colors.grayddd};
    border-radius: 4px;

    &:not(.Mui-disabled):hover,
    &.Mui-focused {
      outline: ${({ theme }) => `1px solid ${theme.colors.gray333}`};
    }

    &.Mui-error {
      border-color: ${({ theme }) => theme.colors.darkRed};
    }
  }
`;

const InputLabel = styled(MuiInputLabel)`
  ${bodyMedium}
  position: static;
  margin-bottom: 0.2rem;
  color: ${({ theme }) => theme.colors.gray333};
  transform: none;

  &.Mui-focused {
    color: ${({ theme }) => theme.colors.gray333};
  }

  &.Mui-error {
    color: ${({ theme }) => theme.colors.darkRed};

    .MuiFormLabel-asterisk {
      color: ${({ theme }) => theme.colors.darkRed};
    }
  }
`;

const StyledMenuOptionContainer = styled(MuiPaper)`
  & .MuiAutocomplete-option {
    overflow-wrap: anywhere;
  }
`;

const InputLabelContainer = styled.div`
  display: flex;
  align-items: baseline;
`;

type ComboboxProps = Omit<
  React.ComponentProps<typeof MuiAutocomplete>,
  | 'defaultValue'
  | 'freeSolo'
  | 'loading'
  | 'loadingText'
  | 'multiple'
  | 'onChange'
  | 'options'
  | 'renderInput'
  | 'value'
> & {
  allowCustomValue?: boolean;
  isReadOnly?: boolean;
  error?: boolean;
  dontSaveSpaceForErrorText?: boolean;
  /**
   * Only displayed when variant === 'form'
   */
  errorText?: string;
  isCompact?: boolean;
  isErrorTextHidden?: boolean;
  isLabelHidden?: boolean;
  isLoading?: boolean;
  isLoadingError?: boolean;
  isSubtextHidden?: boolean;
  label: string;
  loadingErrorText?: string;
  loadingText?: string;
  maxRenderedOptions?: number;
  name?: string;
  /**
   * Aligns with the signature of native html change event handler
   */
  onChange: (event: {
    target: {
      value?: string;
    };
  }) => void;
  options: SelectableOption[];
  placeholder?: string;
  required?: boolean;
  subText?: NullableString;
  textFieldProps?: React.ComponentProps<typeof MuiTextField>;
  tooltipText?: ReactNode;
  value?: string;
  variant?: 'form' | 'underline';
};

export function Combobox({
  allowCustomValue = false,
  dontSaveSpaceForErrorText = false,
  error,
  errorText,
  subText,
  isErrorTextHidden = false,
  isLabelHidden = false,
  isLoading = false,
  isLoadingError = false,
  isSubtextHidden = false,
  label,
  loadingErrorText = `Couldn't load select options`,
  loadingText = 'Loading...',
  maxRenderedOptions,
  name = 'combobox-control',
  onBlur,
  onChange: onChangeProp,
  onFocus,
  openOnFocus = true,
  isReadOnly = false,
  options,
  placeholder,
  required,
  textFieldProps,
  tooltipText,
  value,
  variant = 'form',
  ...props
}: ComboboxProps) {
  const [idRandomify] = useState(uuidv4);
  const valueAsSelectableOption = useMemo(
    () => options.find((opt) => opt?.value === value) || null,
    [options, value],
  );

  const initialInputValue =
    valueAsSelectableOption?.label || (allowCustomValue ? value ?? '' : '');
  const [inputValue, setInputValue] = useState(initialInputValue);

  useEffect(() => setInputValue(initialInputValue), [initialInputValue]);

  const filterOptions = useCallback(
    (allOptions: SelectableOption[], state) => {
      const filtered = allOptions.filter((opt) =>
        opt.label.toLowerCase().includes(state.inputValue?.toLowerCase()),
      );
      return maxRenderedOptions != null
        ? filtered.slice(0, maxRenderedOptions)
        : filtered;
    },
    [maxRenderedOptions],
  );

  const renderInput = useCallback(
    (params: AutocompleteRenderInputParams) => {
      const errorTextId = `${name}-error-text-${idRandomify}`;
      const inputLabelId = `${name}-input-label-${idRandomify}`;
      /* underline variant can't currently have a hidden label */
      return variant === 'underline' ? (
        <UnderlineTextInput
          {...params}
          {...textFieldProps}
          label={label}
          InputLabelProps={{ shrink: true }}
          required={required}
          // ensure readOnly is respected on the underlying input
          inputProps={{
            ...textFieldProps?.inputProps,
            ...params.inputProps,
            readOnly: isReadOnly,
          }}
        />
      ) : (
        <>
          <ConditionalWrapper
            condition={isLabelHidden}
            /* eslint-disable-next-line react/no-unstable-nested-components */
            wrapper={(children) => <VisuallyHidden>{children}</VisuallyHidden>}
          >
            <InputLabelContainer>
              <InputLabel id={inputLabelId} error={error} required={required}>
                {label}
              </InputLabel>
              {tooltipText && <Tooltip title={tooltipText} placement="right" />}
            </InputLabelContainer>
          </ConditionalWrapper>
          <FormTextInput
            {...params}
            {...textFieldProps}
            variant="standard"
            error={error}
            InputProps={{
              ...textFieldProps?.InputProps,
              ...params.InputProps,
              disableUnderline: true,
            }}
            // eslint-disable-next-line react/jsx-no-duplicate-props
            inputProps={{
              ...textFieldProps?.inputProps,
              ...params.inputProps,
              placeholder,
              required,
              'aria-labelledby': inputLabelId,
              'aria-describedby': errorTextId,
              readOnly: isReadOnly,
            }}
          />
          {subText && !isSubtextHidden && (
            <SubText error={error}>{subText}</SubText>
          )}
          <ConditionalWrapper
            condition={!!isErrorTextHidden}
            /* eslint-disable-next-line react/no-unstable-nested-components */
            wrapper={(children) => <VisuallyHidden>{children}</VisuallyHidden>}
          >
            <ErrorText id={errorTextId} error={error}>
              {errorText || (dontSaveSpaceForErrorText ? '' : ' ')}
            </ErrorText>
          </ConditionalWrapper>
        </>
      );
    },
    [
      name,
      idRandomify,
      variant,
      textFieldProps,
      label,
      required,
      isReadOnly,
      isLabelHidden,
      error,
      placeholder,
      subText,
      isErrorTextHidden,
      errorText,
      isSubtextHidden,
      dontSaveSpaceForErrorText,
      tooltipText,
    ],
  );

  return (
    /* this component will only ever allow a single selection */
    <MuiAutocomplete<SelectableOption, false, boolean, boolean>
      PaperComponent={StyledMenuOptionContainer}
      {...props}
      freeSolo={allowCustomValue}
      // Keep the dropdown arrow visible even in freeSolo mode
      forcePopupIcon
      value={valueAsSelectableOption}
      // when read-only, disable every option in the dropdown so selections can't change
      getOptionDisabled={isReadOnly ? () => true : props.getOptionDisabled}
      onChange={(event, option) => {
        if (isReadOnly) {
          return;
        }
        // In freeSolo mode, option can be a string (custom typed value) or SelectableOption
        if (typeof option === 'string') {
          onChangeProp({ target: { value: option } });
        } else {
          onChangeProp({ target: { value: option?.value } });
        }
      }}
      onBlur={(event: FocusEvent<HTMLDivElement>) => {
        // In freeSolo mode, commit the typed value when user blurs the field
        if (allowCustomValue && !isReadOnly && inputValue !== value) {
          // Match case-insensitive option
          const matchingOption = options.find(
            (opt) => opt.label.toLowerCase() === inputValue.toLowerCase(),
          );
          if (matchingOption) {
            onChangeProp({ target: { value: matchingOption.value } });
          } else if (inputValue) {
            onChangeProp({ target: { value: inputValue } });
          } else {
            onChangeProp({ target: { value: undefined } });
          }
        }
        if (onBlur) {
          onBlur(event);
        }
      }}
      onFocus={(event: FocusEvent<HTMLInputElement>) => {
        // setTimeout based on this: https://stackoverflow.com/a/54229871
        const { target } = event;
        setTimeout(() => target.select(), 0);
        if (onFocus) {
          onFocus(event);
        }
      }}
      inputValue={inputValue}
      onInputChange={(event, newInputValue, reason) => {
        if (reason === 'reset') {
          setInputValue(initialInputValue);
          return;
        }
        setInputValue(newInputValue);
      }}
      // only show an 'x' icon to clear if the field is not required and not readonly
      {...(required || isReadOnly ? { closeIcon: null } : [])}
      filterOptions={filterOptions}
      options={options}
      openOnFocus={openOnFocus}
      getOptionSelected={(option, selectedOption) =>
        option.value === selectedOption.value
      }
      getOptionLabel={(option) =>
        // option is a string when it's a custom value
        typeof option === 'string' ? option : option.label
      }
      renderInput={renderInput}
      loading={isLoading || isLoadingError}
      loadingText={
        (isLoading && loadingText) || (isLoadingError && loadingErrorText)
      }
    />
  );
}
